import { FACE_ORDER } from './cube-config.js';
import { CENTER_INDICES_4X4 } from './cube-config-4x4.js';
import {
  CORNER_FACELETS_4X4,
  CORNER_POSITION_NAMES_4X4,
  WING_FACELETS_4X4,
  WING_PIECE_BY_DIRECTED_KEY_4X4,
  WING_POSITION_NAMES_4X4,
  pieceKey4x4,
} from './cubie-config-4x4.js';
import { defaultOrientation4x4, deriveOrientation4x4 } from './orientation-4x4.js';

export const SOLVER_COLOR_NAMES_4X4 = Object.freeze([
  'white', 'red', 'green', 'yellow', 'orange', 'blue',
]);

// cs0x7f Threephase stores centers as U, D, F, B, R, L, clockwise around each
// 2×2 center block. The public facelet matrix remains row-major URFDLB.
export const THREEPHASE_CENTER_FLAT_INDICES_4X4 = Object.freeze([
  5, 6, 10, 9,
  53, 54, 58, 57,
  37, 38, 42, 41,
  85, 86, 90, 89,
  21, 22, 26, 25,
  69, 70, 74, 73,
]);

export const THREEPHASE_CENTER_COLOR_TO_INTERNAL_4X4 = Object.freeze([0, 4, 2, 1, 5, 3]);

function issue(code, message, details = {}) {
  return { code, message, details };
}

function flatIndex([face, index]) {
  return FACE_ORDER.indexOf(face) * 16 + index;
}

function faceletDescription(facelets, addresses) {
  const indices = addresses.map(flatIndex);
  const colorNumbers = indices.map((index) => facelets[index]);
  return {
    addresses: addresses.map(([face, index]) => `${face}${index}`),
    flatIndices: indices,
    colorNumbers,
    colors: colorNumbers.map((color) => SOLVER_COLOR_NAMES_4X4[color] ?? `unknown(${color})`),
    faces: colorNumbers.map((color) => FACE_ORDER[color] ?? '?'),
  };
}

function permutationParity(permutation) {
  let parity = 0;
  for (let left = 0; left < permutation.length; left += 1) {
    for (let right = left + 1; right < permutation.length; right += 1) {
      if (permutation[left] > permutation[right]) parity ^= 1;
    }
  }
  return parity;
}

function duplicateAndMissingIssues(kind, pieces, names) {
  const errors = [];
  const counts = Array(names.length).fill(0);
  for (const piece of pieces) if (Number.isInteger(piece)) counts[piece] += 1;
  for (let piece = 0; piece < names.length; piece += 1) {
    if (counts[piece] > 1) {
      errors.push(issue(`DUPLICATE_${kind}`, `重复${kind === 'CORNER' ? '角块' : '棱翼'}：${names[piece]}（${counts[piece]} 次）`, {
        piece,
        name: names[piece],
        count: counts[piece],
      }));
    }
    if (counts[piece] === 0) {
      errors.push(issue(`MISSING_${kind}`, `缺少${kind === 'CORNER' ? '角块' : '棱翼'}：${names[piece]}`, {
        piece,
        name: names[piece],
      }));
    }
  }
  return errors;
}

function validateFaceletEnvelope(facelets) {
  if (!Array.isArray(facelets) || facelets.length !== 96) {
    return [issue('FACELET_LENGTH', `4×4 求解器状态必须正好包含 96 项，当前为 ${facelets?.length ?? 0} 项`, {
      actual: facelets?.length ?? 0,
      expected: 96,
    })];
  }
  const invalid = facelets
    .map((color, index) => ({ color, index }))
    .filter(({ color }) => !Number.isInteger(color) || color < 0 || color > 5);
  if (invalid.length) {
    return [issue('FACELET_COLOR', `存在 ${invalid.length} 个不在 0–5 范围内的颜色编号`, { invalid })];
  }
  return [];
}

export function analyzeSolverFacelets4x4(facelets) {
  const envelopeErrors = validateFaceletEnvelope(facelets);
  if (envelopeErrors.length) {
    return {
      valid: false,
      errors: envelopeErrors,
      corners: [],
      wings: [],
      centers: [],
    };
  }

  const errors = [];
  const colorCounts = Array(6).fill(0);
  for (const color of facelets) colorCounts[color] += 1;
  for (let color = 0; color < colorCounts.length; color += 1) {
    if (colorCounts[color] !== 16) {
      errors.push(issue('COLOR_COUNT', `${SOLVER_COLOR_NAMES_4X4[color]} 应为 16 张，当前为 ${colorCounts[color]} 张`, {
        color,
        name: SOLVER_COLOR_NAMES_4X4[color],
        count: colorCounts[color],
      }));
    }
  }

  const canonicalCornerFaces = CORNER_FACELETS_4X4.map((group) => group.map(([face]) => face));
  const cornerByColorSet = Object.fromEntries(canonicalCornerFaces.map((faces, index) => [pieceKey4x4(faces.join('')), index]));
  const corners = CORNER_FACELETS_4X4.map((addresses, positionIndex) => {
    const observed = faceletDescription(facelets, addresses);
    const observedKey = pieceKey4x4(observed.faces.join(''));
    const colorSetPieceIndex = cornerByColorSet[observedKey];
    const threephaseOrientation = observed.colorNumbers.findIndex((color) => color === 0 || color === 3);
    let pieceIndex = -1;
    if (threephaseOrientation >= 0) {
      const color1 = observed.colorNumbers[(threephaseOrientation + 1) % 3];
      const color2 = observed.colorNumbers[(threephaseOrientation + 2) % 3];
      pieceIndex = CORNER_FACELETS_4X4.findIndex(([, second, third]) => (
        color1 === Math.floor(flatIndex(second) / 16)
        && color2 === Math.floor(flatIndex(third) / 16)
      ));
    }
    const orientation = threephaseOrientation < 0 ? null : (3 - threephaseOrientation) % 3;
    const result = {
      positionIndex,
      position: CORNER_POSITION_NAMES_4X4[positionIndex],
      ...observed,
      pieceIndex: pieceIndex >= 0 ? pieceIndex : null,
      piece: pieceIndex >= 0 ? CORNER_POSITION_NAMES_4X4[pieceIndex] : null,
      orientation,
      threephaseOrientation: threephaseOrientation >= 0 ? threephaseOrientation : null,
    };
    if (typeof colorSetPieceIndex === 'undefined') {
      errors.push(issue('INVALID_CORNER', `${result.position} 位置出现不存在的角块颜色组合 ${observed.faces.join('')}`, result));
    } else if (pieceIndex < 0) {
      errors.push(issue('MIRRORED_CORNER', `${result.position} 位置的 ${CORNER_POSITION_NAMES_4X4[colorSetPieceIndex]} 角块贴纸顺序被镜像`, {
        ...result,
        colorSetPieceIndex,
        colorSetPiece: CORNER_POSITION_NAMES_4X4[colorSetPieceIndex],
      }));
    }
    return result;
  });

  const wings = WING_FACELETS_4X4.map((addresses, positionIndex) => {
    const observed = faceletDescription(facelets, addresses);
    const directedKey = observed.faces.join('');
    const pieceIndex = WING_PIECE_BY_DIRECTED_KEY_4X4[directedKey];
    const result = {
      positionIndex,
      position: WING_POSITION_NAMES_4X4[positionIndex],
      ...observed,
      directedKey,
      pieceIndex: Number.isInteger(pieceIndex) ? pieceIndex : null,
      piece: Number.isInteger(pieceIndex) ? WING_POSITION_NAMES_4X4[pieceIndex] : null,
    };
    if (!Number.isInteger(pieceIndex)) {
      errors.push(issue('INVALID_WING', `${result.position} 位置出现不存在的棱翼方向组合 ${directedKey}`, result));
    }
    return result;
  });

  const cornerPermutation = corners.map((corner) => corner.pieceIndex);
  const wingPermutation = wings.map((wing) => wing.pieceIndex);
  errors.push(...duplicateAndMissingIssues('CORNER', cornerPermutation, CORNER_POSITION_NAMES_4X4));
  errors.push(...duplicateAndMissingIssues('WING', wingPermutation, WING_POSITION_NAMES_4X4));

  const cornerOrientationSum = corners.reduce((sum, corner) => sum + (corner.orientation ?? 0), 0);
  if (corners.every((corner) => corner.orientation !== null) && cornerOrientationSum % 3 !== 0) {
    errors.push(issue('CORNER_TWIST', `角块朝向总和为 ${cornerOrientationSum}，模 3 不为 0`, {
      sum: cornerOrientationSum,
      modulo: cornerOrientationSum % 3,
    }));
  }

  const centers = FACE_ORDER.flatMap((face) => CENTER_INDICES_4X4.map((index) => {
    const indexFlat = FACE_ORDER.indexOf(face) * 16 + index;
    const colorNumber = facelets[indexFlat];
    return {
      face,
      index,
      address: `${face}${index}`,
      flatIndex: indexFlat,
      colorNumber,
      color: SOLVER_COLOR_NAMES_4X4[colorNumber],
      logicalFace: FACE_ORDER[colorNumber],
    };
  }));
  const centerColorCounts = Array(6).fill(0);
  for (const center of centers) centerColorCounts[center.colorNumber] += 1;
  for (let color = 0; color < centerColorCounts.length; color += 1) {
    if (centerColorCounts[color] !== 4) {
      errors.push(issue('CENTER_COUNT', `${SOLVER_COLOR_NAMES_4X4[color]} 中心块应为 4 个，当前为 ${centerColorCounts[color]} 个`, {
        color,
        name: SOLVER_COLOR_NAMES_4X4[color],
        count: centerColorCounts[color],
      }));
    }
  }

  const threephaseCenters = THREEPHASE_CENTER_FLAT_INDICES_4X4.map((indexFlat, internalPosition) => {
    const colorNumber = facelets[indexFlat];
    return {
      internalPosition,
      flatIndex: indexFlat,
      address: `${FACE_ORDER[Math.floor(indexFlat / 16)]}${indexFlat % 16}`,
      colorNumber,
      color: SOLVER_COLOR_NAMES_4X4[colorNumber],
      internalColor: THREEPHASE_CENTER_COLOR_TO_INTERNAL_4X4[colorNumber],
    };
  });

  const completeCornerPermutation = cornerPermutation.every(Number.isInteger);
  const completeWingPermutation = wingPermutation.every(Number.isInteger);
  const phase2FirstHalf = wingPermutation.slice(0, 12);
  const phase2PairKeys = phase2FirstHalf.map((piece) => (Number.isInteger(piece) ? piece % 12 : null));
  const phase2Ready = completeWingPermutation
    && new Set(phase2PairKeys).size === 12
    && phase2FirstHalf.filter((piece) => piece >= 12).length % 2 === 0;
  return {
    valid: errors.length === 0,
    errors,
    faceOrder: FACE_ORDER.join(''),
    colorNumbering: Object.fromEntries(SOLVER_COLOR_NAMES_4X4.map((name, number) => [name, number])),
    colorCounts: Object.fromEntries(SOLVER_COLOR_NAMES_4X4.map((name, number) => [name, colorCounts[number]])),
    corners,
    wings,
    centers,
    threephaseCenters,
    cornerOrientationSum,
    cornerOrientationModulo: cornerOrientationSum % 3,
    cornerPermutationParity: completeCornerPermutation ? permutationParity(cornerPermutation) : null,
    wingPermutationParity: completeWingPermutation ? permutationParity(wingPermutation) : null,
    threephasePhase2Ready: phase2Ready,
    threephasePhase2FirstHalf: phase2FirstHalf,
    centerColorCounts: Object.fromEntries(SOLVER_COLOR_NAMES_4X4.map((name, number) => [name, centerColorCounts[number]])),
    reachability: {
      observableConstraintsSatisfied: errors.length === 0,
      note: '4×4 允许奇棱翼排列（parity）；同色中心不可区分，因此可见贴纸不能固定唯一中心排列奇偶性。',
    },
  };
}

export function solverFaceletsToStickers4x4(facelets, rawOrientation = defaultOrientation4x4()) {
  const envelopeErrors = validateFaceletEnvelope(facelets);
  if (envelopeErrors.length) throw new TypeError(envelopeErrors[0].message);
  const orientation = rawOrientation.faceToColor ? rawOrientation : deriveOrientation4x4(rawOrientation);
  return Object.fromEntries(FACE_ORDER.map((face, faceIndex) => [
    face,
    facelets.slice(faceIndex * 16, faceIndex * 16 + 16).map((logicalColor) => (
      orientation.faceToColor[FACE_ORDER[logicalColor]]
    )),
  ]));
}

function transformMatrix(matrix, quarterTurns, mirrored) {
  const normalizedTurns = ((quarterTurns % 4) + 4) % 4;
  const transformed = Array(16);
  for (let sourceIndex = 0; sourceIndex < 16; sourceIndex += 1) {
    let row = Math.floor(sourceIndex / 4);
    let col = sourceIndex % 4;
    if (mirrored) col = 3 - col;
    for (let turn = 0; turn < normalizedTurns; turn += 1) [row, col] = [col, 3 - row];
    transformed[row * 4 + col] = matrix[sourceIndex];
  }
  return transformed;
}

export function transformSolverFace4x4(facelets, face, { quarterTurns = 0, mirrored = false } = {}) {
  const envelopeErrors = validateFaceletEnvelope(facelets);
  if (envelopeErrors.length) throw new TypeError(envelopeErrors[0].message);
  if (!FACE_ORDER.includes(face)) throw new TypeError(`未知魔方面：${face}`);
  const faceOffset = FACE_ORDER.indexOf(face) * 16;
  const result = [...facelets];
  result.splice(faceOffset, 16, ...transformMatrix(result.slice(faceOffset, faceOffset + 16), quarterTurns, mirrored));
  return result;
}

export function enumerateSolverFaceTransforms4x4(facelets, face) {
  return [false, true].flatMap((mirrored) => Array.from({ length: 4 }, (_, quarterTurns) => {
    const transformedFacelets = transformSolverFace4x4(facelets, face, { quarterTurns, mirrored });
    return {
      face,
      mirrored,
      quarterTurns,
      facelets: transformedFacelets,
      analysis: analyzeSolverFacelets4x4(transformedFacelets),
    };
  }));
}
